import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CreateResourceApi, UpdateResourceApi } from '../../../reduxToolkit/Cards/CardsApi';
import { UploadImage } from '../../../reduxToolkit/ShareSlice/shareApi';
import { CustomTextField } from '../../../components/CustomTextField';
import { FILE_TYPE } from '../../../Helpers/fileType';
import CustomModal from '../../../components/Modal/CustomModal';
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../components/PhotoPicker/SubComponent/Models/Models';
import {ACTION_MODEL} from '../../../Helpers/ActionModel';
import CustomPhotoPicker from '../../../components/PhotoPicker/CustomPhotoPicker';
import noLogo from '../../../assets/defaultImages/no-logo.png';
import {errorMsg} from '../../../Apis/functions';
import {TABLES} from "../../../Apis/Tables";
/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 *
 * @param boolean open: shows the modal
 * @param event onClose: closes the modal
 * @param event onUpdated: reloads the resources list
 * @param object resource: resource to update or to inspect, null on the create action
 * @param boolean disabled: shows the resource without the edit controls
 */
const AddResource = ({ open, onClose, onUpdated, resource = null, disabled = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [resourceName, setResourceName] = useState('');
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL, preview: noLogo });
    const [emptyFields, setEmptyFields] = useState(false);
    const isUpdate = resource !== null && !disabled;
    // Loads the selected resource, or clears the form when the modal is opened to create.
    useEffect(() => {
        setEmptyFields(false);
        if (resource !== null) {
            setResourceName(resource?.name || '');
            setPhotoPicker({
                ...PHOTO_PICKER_MODEL,
                id: resource?.id,
                preview: resource?.image != null ? `data:image/png;base64,${resource.image}` : noLogo,
            });
        } else {
            setResourceName('');
            handleClearPhoto();
        }
    }, [open, resource]);
    // Clears the selected image by setting the state to null.
    const handleClearPhoto = () => {
        setPhotoPicker({...PHOTO_PICKER_MODEL, preview: noLogo});
    };
    // Uploads the image of the resource, the id comes from the created or updated resource.
    const handleUploadImage = (id, selectedImage, successMessage) => {
        let formData = new FormData();
        formData.append('id', id);
        formData.append('option', TABLES.RESOURCES);
        formData.append('file', selectedImage);
        dispatch(UploadImage(formData)).then(
            ({
                 payload: {
                     data: { success, code },
                 },
             }) => {
                if (success === true) {
                    toast.success(t(successMessage));
                    onUpdated();
                    onClose();
                } else {
                    errorMsg(code)
                }
            },
        );
    };
    // Initiates the upload process for the image if all conditions are met.
    const handelUploadImageAction = () => {
        setEmptyFields(true);
        let selectedImage = getPhotoPicker(photoPicker);
        if (resourceName !== '' && selectedImage !== null) {
            const body = {
                name: resourceName,
            };
            dispatch(CreateResourceApi(body)).then(({payload: {data: { data, success, code }}}) => {
                    if (success === true) {
                        handleUploadImage(data?.id, selectedImage, 'has_been_created_successfully');
                    } else {
                        errorMsg(code)
                    }
                },
            );
        } else if (selectedImage === null) {
            toast.warn(t('please_select_an_image'));
        }
    };
    // Updates the resource, the image is only sent when a new one was selected.
    const handleUpdateAction = () => {
        setEmptyFields(true);
        let selectedImage = getPhotoPicker(photoPicker);
        const body = {
            id: resource?.id,
            name: resourceName,
        };
        dispatch(UpdateResourceApi(body)).then(({payload: {data: { data, success, code }}}) => {
                if (success === true) {
                    if (selectedImage !== null) {
                        handleUploadImage(data?.id || resource?.id, selectedImage, 'has_been_updated_successfully');
                    } else {
                        toast.success(t('has_been_updated_successfully'));
                        onUpdated();
                        onClose();
                    }
                } else {
                    errorMsg(code)
                }
            },
        );
    };

    return (
        <CustomModal
            title={disabled ? 'details' : isUpdate ? 'update' : 'create'}
            size='md'
            buttonCancelText={disabled ? 'close' : 'cancel'}
            buttonConfirm='confirm'
            cancelButton={true}
            confirmButton={!disabled}
            onHide={onClose}
            onConfirm={() => {
                setEmptyFields(true);
                if (resourceName.trim() === '') {
                    toast.warn(t('please_fill_all_fields'));
                    return;
                }
                if (isUpdate) {
                    handleUpdateAction();
                } else {
                    handelUploadImageAction();
                }
            }}
            show={open}
            classNameBody="pt-2"
        >
            <Box className={`form-field ${disabled ? '' : 'required-data'} mt-2`}>
                <CustomTextField
                    size='small'
                    fullWidth
                    label='name'
                    id='txt_ar_name'
                    value={resourceName}
                    onChange={(e) => setResourceName(e.target.value)}
                    inputProps={{ maxLength: 128 }}
                    required={!disabled}
                    disabled={disabled}
                    emptyFields={emptyFields && resourceName === ''}
                    trans='name'
                />
            </Box>

            <div className='row'>
                <div className='d-flex justify-content-center align-items-center'>
                    <CustomPhotoPicker
                        id='file_input'
                        photoPicker={photoPicker}
                        setPhotoPicker={setPhotoPicker}
                        actionModel={ACTION_MODEL.CREATE}
                        accept={{ ...FILE_TYPE.IMAGE }}
                        classWith={'w-100'}
                        titleInput='choose_or_drag_your_image_here'
                        required={!disabled}
                        disabled={disabled}
                        showBtnDelete={!disabled}
                    />
                </div>
            </div>
        </CustomModal>
    );
};

export default AddResource;
